import { useCallback } from 'react';
import { useFig } from '../../session/FigProvider.js';
import type { ProviderConnection } from '../../core/api/types.js';
import { EmptyState, ErrorState, RowSkeleton } from '../../components/States.js';
import { useResource } from './useResource.js';

/** Provider connections, read-only for now — creating one needs the customer's own API key. */
export function Connections() {
  const { client } = useFig();
  const load = useCallback(
    () => client.listConnections({ sortBy: 'ConnectionName', sortDirection: 'Asc' }), [client]);
  const { data, error, busy, reload } = useResource<readonly ProviderConnection[]>(load);

  if (error !== null) return <ErrorState error={error} onRetry={() => void reload()} />;

  return (
    <>
      <h1 className="page-title">Connections</h1>
      {data === null && busy && <RowSkeleton count={2} />}
      {data?.length === 0 && (
        <EmptyState title="Nothing connected yet"
          body="Connect a client’s ElevenLabs account in the Fig app to start bringing their calls in." />
      )}
      {data && data.length > 0 && (
        <table className="table">
          <caption className="sr-only">Provider connections, {data.length}</caption>
          <thead>
            <tr><th scope="col">Connection</th><th scope="col">Syncs</th>
              <th scope="col">Status</th><th scope="col">Last sync</th></tr>
          </thead>
          <tbody>
            {data.map((c) => (
              <tr key={c.id}>
                <th scope="row" className="cell-name">{c.connectionName}</th>
                <td>{c.syncSchedule === 'Every15Minutes' ? 'Every 15 minutes' : 'Hourly'}</td>
                <td>
                  {/* SyncStatus.Failed existed since Phase 2 and surfaced nowhere. A product
                      that lets someone discover missing data themselves is not premium. */}
                  <span className={`pill pill-${c.syncStatus.toLowerCase()}`}>
                    {c.syncInProgress ? 'Syncing now' : c.syncStatus === 'Failed' ? 'Last sync failed' : c.syncStatus}
                  </span>
                </td>
                <td>{c.lastSyncedAt ? new Date(c.lastSyncedAt).toLocaleString() : 'Never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
